/**
 * LUNA Coding Tutor - Dashboard Hook
 * 원본: assets/js/dashboard.js (1087 lines)의 모든 로직을 React Hook으로 완벽하게 포팅
 * 
 * ===== 로직 매핑 테이블 =====
 * renderActivityChart()        -> getActivityChartConfig()
 * renderSkillChart()           -> getSkillChartConfig()
 * renderSkillChartForLanguage() -> getSkillChartForLanguage()
 * renderTimeline()             -> timelineData (상태)
 * updateStats()                -> stats (상태), animateValue (유틸)
 * renderInsights()             -> insights (상태)
 * renderTrendSummary()         -> trend (상태)
 * renderLanguageCompareChart() -> getLanguageCompareChartConfig()
 * renderProblemStats()         -> problemStats (상태)
 * renderMasteredSkills()       -> masteredSkills (상태)
 * loadRoadmapPreview()         -> roadmapPreview (상태)
 * loadMessages()               -> messages (상태)
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import api from '@/services/api';

// ============================================
// Chart.js 전역 설정 (원본 동일)
// ============================================

/**
 * Chart.js 글로벌 defaults 설정
 * 컴포넌트에서 Chart.defaults로 접근
 */
export const CHART_DEFAULTS = {
  color: '#94a3b8',
  borderColor: 'rgba(255, 255, 255, 0.05)',
  fontFamily: "'Noto Sans KR', sans-serif",
};

// ============================================
// 유틸리티 함수들 (원본 dashboard.js에서 추출)
// ============================================

/**
 * HTML 이스케이프 (XSS 방지)
 * 원본: escapeHtml()
 */
export function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * 숫자 카운트업 애니메이션 훅
 * 원본: animateValue()
 */
export function useAnimatedValue(targetValue, duration = 800, formatNumber = false) {
  const [displayValue, setDisplayValue] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    const startTime = performance.now();
    const startValue = 0;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // easeOutQuart 이징 (원본 동일)
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(startValue + (targetValue - startValue) * easeProgress);

      setDisplayValue(current);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [targetValue, duration]);

  return formatNumber ? displayValue.toLocaleString() : displayValue;
}

/**
 * 메시지 날짜 포맷 (원본 formatMessageDate())
 */
export function formatMessageDate(isoString) {
  const date = new Date(isoString);
  const now = new Date();
  const diff = now - date;

  // 1시간 이내
  if (diff < 3600000) {
    const mins = Math.floor(diff / 60000);
    return mins <= 0 ? '방금 전' : `${mins}분 전`;
  }
  // 오늘
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  }
  // 올해
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  }
  // 그 외
  return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ============================================
// 차트 설정 생성 함수들
// ============================================

/**
 * 활동 차트 설정 생성
 * 원본: renderActivityChart()
 */
export function getActivityChartConfig(heatmapData) {
  // 최근 14일 날짜 생성
  const labels = [];
  const data = [];
  const today = new Date();

  for (let i = 13; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const displayDate = `${date.getMonth() + 1}/${date.getDate()}`;

    labels.push(displayDate);
    data.push(heatmapData?.[dateStr] || 0);
  }

  return {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: '획득 XP',
        data,
        borderColor: '#22d3ee', // Cyan-400
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return 'rgba(34, 211, 238, 0.1)';

          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
          gradient.addColorStop(0, 'rgba(34, 211, 238, 0)');
          gradient.addColorStop(0.5, 'rgba(34, 211, 238, 0.1)');
          gradient.addColorStop(1, 'rgba(34, 211, 238, 0.3)');
          return gradient;
        },
        borderWidth: 3,
        tension: 0.4, // Smooth curves
        fill: true,
        pointBackgroundColor: '#0B0E14',
        pointBorderColor: '#22d3ee',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: '#22d3ee',
        pointHoverBorderColor: '#fff',
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          titleColor: '#e2e8f0',
          bodyColor: '#94a3b8',
          borderColor: 'rgba(34, 211, 238, 0.3)',
          borderWidth: 1,
          padding: 12,
          displayColors: false,
          callbacks: {
            label: (context) => `${context.parsed.y} XP 획득`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10, family: "'Space Mono', monospace" }, color: '#64748b' },
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(255, 255, 255, 0.03)' },
          ticks: {
            font: { size: 10, family: "'Space Mono', monospace" },
            color: '#64748b',
            callback: (value) => value + ' XP',
          },
        },
      },
    },
  };
}

/**
 * 스킬 레이더 차트 설정 생성
 * 원본: renderSkillChart()
 */
export function getSkillChartConfig(skillData = null) {
  const labels = ['문법 이해', '논리 구성', '문제 해결', '코드 품질', '학습 꾸준함'];
  const data = Array.isArray(skillData) ? skillData : [50, 50, 50, 50, 50];

  return {
    type: 'radar',
    data: {
      labels,
      datasets: [{
        label: '내 능력치',
        data,
        borderColor: '#a855f7', // Purple-500
        backgroundColor: 'rgba(168, 85, 247, 0.25)',
        borderWidth: 2,
        pointBackgroundColor: '#a855f7',
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        pointRadius: 3,
        pointHoverRadius: 5,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          titleColor: '#e2e8f0',
          bodyColor: '#94a3b8',
          borderColor: 'rgba(168, 85, 247, 0.3)',
          borderWidth: 1,
          padding: 12,
        },
      },
      scales: {
        r: {
          beginAtZero: true,
          max: 100,
          grid: { color: 'rgba(255, 255, 255, 0.05)', circular: true },
          angleLines: { color: 'rgba(255, 255, 255, 0.05)' },
          pointLabels: { font: { size: 11, weight: '600' }, color: '#cbd5e1' },
          ticks: { display: false, stepSize: 20 },
        },
      },
    },
  };
}

/**
 * 언어별 스킬 차트 데이터 생성
 * 원본: renderSkillChartForLanguage()
 */
export function getSkillDataForLanguage(lang, globalStats, languageStats) {
  if (lang === 'all') {
    return globalStats || [50, 50, 50, 50, 50];
  }

  const entry = languageStats?.[lang.toLowerCase()] || null;
  if (!entry) {
    return globalStats || [50, 50, 50, 50, 50];
  }

  return [
    Math.round(entry.syntax_score || entry.syntax || 50),
    Math.round(entry.logic_score || entry.logic || 50),
    Math.round(entry.problem_solving_score || entry.problem_solving || 50),
    Math.round(entry.code_quality_score || entry.code_quality || 50),
    Math.round(globalStats ? globalStats[4] : 50), // persistence는 통합 기준 사용
  ];
}

/**
 * 언어별 비교 차트 설정 생성
 * 원본: renderLanguageCompareChart()
 */
export function getLanguageCompareChartConfig(languageStats) {
  if (!languageStats || Object.keys(languageStats).length === 0) {
    return null;
  }

  const languages = Object.keys(languageStats);
  const colors = {
    python: '#38bdf8', // Sky-400
    javascript: '#facc15', // Yellow-400
    java: '#f97316', // Orange-500
    c: '#94a3b8', // Slate-400
    cpp: '#60a5fa', // Blue-400
    default: '#a855f7', // Purple-500
  };

  // 각 언어의 평균 점수 계산
  const avgScores = languages.map((lang) => {
    const data = languageStats[lang];
    if (!data) return 0;
    return Math.round(
      ((data.syntax_score || 50) +
        (data.logic_score || 50) +
        (data.problem_solving_score || 50) +
        (data.code_quality_score || 50)) / 4
    );
  });

  const problemCounts = languages.map((lang) => {
    const data = languageStats[lang];
    return data?.problems_count || 0;
  });

  return {
    type: 'bar',
    data: {
      labels: languages.map((l) => l.toUpperCase()),
      datasets: [
        {
          label: '평균 능력치',
          data: avgScores,
          backgroundColor: languages.map((l) => colors[l.toLowerCase()] || colors.default),
          borderRadius: 6,
          borderSkipped: false,
          barPercentage: 0.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          titleColor: '#e2e8f0',
          bodyColor: '#94a3b8',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          padding: 10,
          callbacks: {
            afterLabel: (context) => {
              const idx = context.dataIndex;
              return `문제 수: ${problemCounts[idx]}개`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          max: 100,
          grid: { color: 'rgba(255, 255, 255, 0.03)' },
          ticks: { font: { size: 10, family: "'Space Mono', monospace" }, color: '#64748b' },
        },
        y: {
          grid: { display: false },
          ticks: { font: { size: 11, weight: 'bold' }, color: '#e2e8f0' },
        },
      },
    },
  };
}

// ============================================
// 메인 Dashboard Hook
// ============================================

/**
 * 대시보드 데이터 및 로직을 관리하는 훅
 */
export function useDashboard() {
  // 로딩/에러 상태
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 통계 데이터
  const [stats, setStats] = useState({
    level: 1,
    totalXp: 0,
    streak: 0,
    requiredXp: 100,
  });

  // 차트 데이터
  const [heatmapData, setHeatmapData] = useState({});
  const [globalStats, setGlobalStats] = useState([50, 50, 50, 50, 50]);
  const [languageStats, setLanguageStats] = useState({});
  const [learnedLanguages, setLearnedLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('all');

  // 타임라인 데이터
  const [timeline, setTimeline] = useState([]);

  // 인사이트 데이터
  const [insights, setInsights] = useState(null);
  const [trend, setTrend] = useState(null);

  // 문제 해결 통계
  const [problemStats, setProblemStats] = useState({
    attempted: 0,
    solved: 0,
    successRate: 0,
  });

  // 보유 스킬
  const [masteredSkills, setMasteredSkills] = useState([]);

  // 로드맵 프리뷰
  const [roadmapPreview, setRoadmapPreview] = useState(null);

  // 메시지 관련
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messagesPanelOpen, setMessagesPanelOpen] = useState(false);

  // XP 진행률 계산
  const xpProgress = useMemo(() => {
    const { totalXp, requiredXp } = stats;
    return requiredXp > 0 ? Math.min((totalXp / requiredXp) * 100, 100) : 0;
  }, [stats]);

  // 선택된 언어에 따른 스킬 데이터
  const currentSkillData = useMemo(() => {
    return getSkillDataForLanguage(selectedLanguage, globalStats, languageStats);
  }, [selectedLanguage, globalStats, languageStats]);

  /**
   * 대시보드 데이터 로드
   * 원본: initDashboard()
   */
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await api.getDashboard();

      // 통계 업데이트
      setStats({
        level: data?.level || 1,
        totalXp: data?.total_xp || 0,
        streak: data?.streak || 0,
        requiredXp: data?.required_xp || 100,
      });

      // 차트 데이터 저장
      setHeatmapData(data?.heatmap || {});
      setGlobalStats(data?.stats || [50, 50, 50, 50, 50]);
      setLanguageStats(data?.language_stats || {});
      setLearnedLanguages(data?.learned_languages || []);

      // 타임라인
      setTimeline(data?.timeline || []);

      // 인사이트
      setInsights(data?.insights || null);
      setTrend(data?.trend || null);

      // 문제 해결 통계
      const totalProblems = data?.total_problems || {};
      setProblemStats({
        attempted: totalProblems.attempted || 0,
        solved: totalProblems.solved || 0,
        successRate: totalProblems.success_rate || 0,
      });

      // 보유 스킬
      setMasteredSkills(data?.mastered_skills || []);

      setLoading(false);
    } catch (err) {
      console.error('[DASHBOARD] 데이터 로드 실패:', err);

      // 인증 오류 감지
      if (err.message?.includes('401') || err.message?.includes('로그인')) {
        setError({ type: 'auth', message: '로그인이 필요합니다.' });
      } else {
        setError({ type: 'general', message: err.message || '알 수 없는 오류' });
      }

      setLoading(false);
    }
  }, []);

  /**
   * 로드맵 프리뷰 로드
   * 원본: loadRoadmapPreview()
   */
  const loadRoadmapPreview = useCallback(async () => {
    try {
      const res = await api.getRoadmap();

      if (res?.has_roadmap && res?.roadmap) {
        const roadmap = res.roadmap;
        const phases = roadmap.phases || [];
        const totalTopics = phases.reduce((sum, p) => sum + (p.topics?.length || 0), 0);
        const completedTopics = phases.reduce((sum, p) => {
          return sum + (p.topics?.filter((t) => t.status === 'completed').length || 0);
        }, 0);

        // 현재 활성 토픽 찾기
        let currentTopic = null;
        for (const phase of phases) {
          const active = phase.topics?.find((t) => t.status === 'active');
          if (active) {
            currentTopic = { ...active, phase: phase.title };
            break;
          }
        }

        setRoadmapPreview({
          hasRoadmap: true,
          title: roadmap.roadmap_title || '학습 로드맵',
          description: roadmap.description || '',
          phasesCount: phases.length,
          totalTopics,
          completedTopics,
          progress: roadmap.total_progress || 0,
          currentTopic,
        });
      } else {
        setRoadmapPreview({ hasRoadmap: false });
      }
    } catch (e) {
      console.warn('[DASHBOARD] 로드맵 프리뷰 로드 실패:', e);
      setRoadmapPreview({ hasRoadmap: false, error: true });
    }
  }, []);

  /**
   * 메시지 로드
   * 원본: loadMessages()
   */
  const loadMessages = useCallback(async () => {
    try {
      const data = await api.getUserMessages();
      setMessages(data?.messages || []);
      setUnreadCount(data?.unread_count || 0);
    } catch (err) {
      console.warn('[DASHBOARD] 메시지 로드 실패:', err);
    }
  }, []);

  /**
   * 메시지 읽음 표시
   */
  const markMessageAsRead = useCallback(async (messageId) => {
    try {
      await api.markMessageAsRead(messageId);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, is_read: true } : msg
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.warn('읽음 표시 실패:', err);
    }
  }, []);

  /**
   * 모든 메시지 읽음 표시
   */
  const markAllMessagesAsRead = useCallback(async () => {
    try {
      await api.markAllMessagesAsRead();
      setMessages((prev) => prev.map((msg) => ({ ...msg, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.warn('모두 읽음 처리 실패:', err);
    }
  }, []);

  /**
   * 추세 요약 데이터 가공
   * 원본: renderTrendSummary()
   */
  const trendSummary = useMemo(() => {
    if (!trend) return null;

    const growthRate = trend.growth_rate || 0;
    const recentXp = trend.recent_7days_xp || 0;
    const prevXp = trend.previous_7days_xp || 0;

    let icon, color, text;
    if (growthRate > 10) {
      icon = 'fa-arrow-trend-up';
      color = 'text-green-400';
      text = `지난 주 대비 ${Math.abs(growthRate).toFixed(0)}% 증가`;
    } else if (growthRate < -10) {
      icon = 'fa-arrow-trend-down';
      color = 'text-red-400';
      text = `지난 주 대비 ${Math.abs(growthRate).toFixed(0)}% 감소`;
    } else {
      icon = 'fa-minus';
      color = 'text-slate-400';
      text = '지난 주와 비슷한 수준';
    }

    return { icon, color, text, recentXp, prevXp };
  }, [trend]);

  /**
   * 언어별 문제 수 데이터 가공
   */
  const languageProblemsList = useMemo(() => {
    if (!languageStats) return [];

    const langs = Object.entries(languageStats).sort(
      (a, b) => (b[1]?.problems_count || 0) - (a[1]?.problems_count || 0)
    );

    const totalCount = langs.reduce(
      (sum, [_, data]) => sum + (data?.problems_count || 0),
      0
    );

    return langs.map(([lang, data]) => {
      const count = data?.problems_count || 0;
      const percent = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
      return { lang, count, percent };
    });
  }, [languageStats]);

  return {
    // 상태
    loading,
    error,
    stats,
    xpProgress,
    heatmapData,
    globalStats,
    languageStats,
    learnedLanguages,
    selectedLanguage,
    currentSkillData,
    timeline,
    insights,
    trend,
    trendSummary,
    problemStats,
    masteredSkills,
    languageProblemsList,
    roadmapPreview,
    messages,
    unreadCount,
    messagesPanelOpen,

    // 액션
    setSelectedLanguage,
    setMessagesPanelOpen,
    loadDashboard,
    loadRoadmapPreview,
    loadMessages,
    markMessageAsRead,
    markAllMessagesAsRead,

    // 차트 설정 생성 함수
    getActivityChartConfig: () => getActivityChartConfig(heatmapData),
    getSkillChartConfig: () => getSkillChartConfig(currentSkillData),
    getLanguageCompareChartConfig: () => getLanguageCompareChartConfig(languageStats),
  };
}

export default useDashboard;
